import map from "lodash/map";
import merge from "lodash/merge";
import moment from "moment";
import { Gantt } from "PFApp/components/gantt";
import { useRoleJobCode } from "PFApp/use_role_job_code";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useProfilesBookings } from "PFCore/hooks/queries/profile/use_profiles_bookings";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { AvailabilityInfoPanel } from "./availability_info_panel";
import { ProfileLabel } from "./profile_label";

const CompareAvailabilityPage = ({
  comparableProfiles,
  startDate,
  availabilityInfo,
  removeProfileId,
  isUnbiasedMode
}) => {
  const {
    data: { availability }
  } = useMyAccountInfo();
  const jobCodeDisplayAs = useRoleJobCode();
  const { formatISODate } = useDateFormatter();

  const monthsLimit = useMemo(() => availability?.calculated_months_limit || 6, [availability]);

  const endDate = useMemo(
    () => formatISODate(moment(startDate).add(monthsLimit, "months").startOf("isoWeek").set("hour", 12)),
    [startDate, monthsLimit, formatISODate]
  );

  const { bookingsByProfileId } = useProfilesBookings({
    profiles: map(comparableProfiles, ({ profile }) => ({ id: profile.id, rowKey: String(profile.id) })),
    dateRange: { min: startDate, max: endDate }
  });

  const rowItems = useMemo(
    () =>
      map(comparableProfiles, (comparableProfile) => {
        const { profile } = comparableProfile;

        return merge({}, comparableProfile, {
          profile: {
            availability: {
              bookings: bookingsByProfileId[profile.id] || []
            }
          }
        });
      }),
    [bookingsByProfileId, comparableProfiles]
  );

  return (
    <Gantt
      monthsLimit={monthsLimit}
      rowItems={rowItems}
      startDate={startDate}
      availabilityInfo={availabilityInfo}
      removeProfileId={removeProfileId}
      infoPanel={<AvailabilityInfoPanel availability={availabilityInfo} />}
      rowItemLabelComponent={(shortlist) => (
        <ProfileLabel
          key={shortlist.id}
          shortlist={shortlist}
          removeProfileId={removeProfileId}
          isUnbiasedMode={isUnbiasedMode}
        />
      )}
      jobCodeDisplayAs={jobCodeDisplayAs}
    />
  );
};

CompareAvailabilityPage.propTypes = {
  comparableProfiles: PropTypes.arrayOf(PropTypes.shape({ profile: PropTypes.object })).isRequired,
  startDate: PropTypes.instanceOf(Date).isRequired,
  availabilityInfo: PropTypes.object.isRequired, // see AvailabilityInfoPanel for details
  removeProfileId: PropTypes.func.isRequired,
  isUnbiasedMode: PropTypes.bool
};

export default CompareAvailabilityPage;
